const SCRIPTLET_REGEX = /<%[\s\S]*?%>/g;
const PLACEHOLDER_REGEX =
  /<span\b[^>]*data-regmail-scriptlet="([^"]+)"[^>]*>(?:[\s\S]*?)<\/span>/gi;
const BASE64_CLEAN_REGEX = /^[A-Za-z0-9+/]+={0,2}$/;
const BASE64_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

const decodeHtmlEntities = value => {
  if (typeof value !== 'string' || !value) return value || '';
  if (typeof document === 'undefined') return value;
  try {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value;
    return textarea.value || value;
  } catch {
    return value;
  }
};

const normalizeBase64Input = value => {
  if (typeof value !== 'string' || !value) return '';
  let normalized = decodeHtmlEntities(value).trim();
  if (!normalized) return '';
  normalized = normalized
    .replace(/\s+/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const remainder = normalized.length % 4;
  if (remainder === 1) return '';
  if (remainder > 1) {
    normalized = normalized.padEnd(normalized.length + (4 - remainder), '=');
  }
  if (!BASE64_CLEAN_REGEX.test(normalized)) return '';
  return normalized;
};

const decodeBase64Bytes = value => {
  if (!value) return new Uint8Array(0);
  let input = value.replace(/=+$/g, '');
  if (!input) return new Uint8Array(0);
  const bytes = [];
  let buffer = 0;
  let bits = 0;

  for (let i = 0; i < input.length; i += 1) {
    const char = input.charAt(i);
    const index = BASE64_ALPHABET.indexOf(char);
    if (index < 0) return new Uint8Array(0);
    buffer = (buffer << 6) | index;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }

  return new Uint8Array(bytes);
};

const decodeUtf8Bytes = bytes => {
  if (!bytes || bytes.length === 0) return '';
  try {
    if (typeof TextDecoder !== 'undefined') {
      return new TextDecoder('utf-8').decode(bytes);
    }
  } catch {
    // fallback below
  }
  try {
    const ascii = Array.from(bytes)
      .map(byte => String.fromCharCode(byte))
      .join('');
    return decodeURIComponent(
      ascii
        .split('')
        .map(char =>
          `%${char.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`
        )
        .join('')
    );
  } catch {
    return '';
  }
};

const toBase64 = value => {
  if (typeof value !== 'string' || !value) return '';
  try {
    if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
      return window.btoa(
        encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, (_, p1) =>
          String.fromCharCode(parseInt(p1, 16))
        )
      );
    }
  } catch {
    // fall through to Buffer
  }
  if (typeof Buffer !== 'undefined' && typeof Buffer.from === 'function') {
    return Buffer.from(value, 'utf8').toString('base64');
  }
  return '';
};

const fromBase64 = value => {
  const normalizedValue = normalizeBase64Input(value);
  if (!normalizedValue) return '';
  if (typeof Buffer !== 'undefined' && typeof Buffer.from === 'function') {
    try {
      return Buffer.from(normalizedValue, 'base64').toString('utf8');
    } catch {
      // fall through to manual decoder
    }
  }
  const bytes = decodeBase64Bytes(normalizedValue);
  return decodeUtf8Bytes(bytes);
};

export const wrapScriptletsWithPlaceholders = html => {
  if (typeof html !== 'string' || !html.trim()) {
    return html || '';
  }
  return html.replace(SCRIPTLET_REGEX, match => {
    const encoded = toBase64(match);
    if (!encoded) return match;
    return `<span class="kit-scriptlet-placeholder" data-regmail-scriptlet="${encoded}" contenteditable="false" role="note" aria-label="Regmail scriptlet">Regmail scriptlet</span>`;
  });
};

export const unwrapScriptletPlaceholders = html => {
  if (typeof html !== 'string' || !html.trim()) {
    return html || '';
  }
  try {
    return html.replace(PLACEHOLDER_REGEX, (fullMatch, encoded) => {
      try {
        const restored = fromBase64(encoded);
        return restored || fullMatch;
      } catch {
        return fullMatch;
      }
    });
  } catch {
    return html;
  }
};
